/**
 * SalesManagerCard Component
 *
 * Main container for the Sales Manager Performance dashboard.
 * Includes the sortable manager list and pie chart visualization.
 * Blue theme to match NEW_VEHICLE department.
 */

import React from 'react';
import {
  UserGroupIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../LoadingSpinner';
import {
  SalesManager,
  SalesManagerSummary,
  SortConfig,
} from './types';
import { SalesManagerList } from './SalesManagerList';
import { SalesManagerPieChart } from './SalesManagerPieChart';
import { LookToBookPieChart } from './LookToBookPieChart';
import { VAutoLookToBookResponse } from '../../../services/api';

interface SalesManagerCardProps {
  managers: SalesManager[];
  summary: SalesManagerSummary | null;
  loading: boolean;
  refreshing?: boolean;
  error: string | null;
  year: number;
  month: number;
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
  onRefresh?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showHeaderTotals?: boolean;
  // vAuto Look to Book — optional second pie chart in the right column.
  // Source: vAuto Appraisal Summary Report (past 60 days).
  lookToBookData?: VAutoLookToBookResponse | null;
  lookToBookLoading?: boolean;
}

/**
 * Get month name from number
 */
function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

/**
 * Format currency for display
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export const SalesManagerCard: React.FC<SalesManagerCardProps> = ({
  managers,
  summary,
  loading,
  refreshing = false,
  error,
  year,
  month,
  sortConfig,
  onSortChange,
  onRefresh,
  collapsed = false,
  onToggleCollapse,
  showHeaderTotals = true,
  lookToBookData,
  lookToBookLoading = false,
}) => {
  const totalGross = summary?.totalGross ??
    managers.reduce((sum, m) => sum + m.totalGross, 0);

  const showLookToBook = !!(lookToBookData?.available && lookToBookData.appraisers.length > 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Sales Manager Performance
              </h3>
              <p className="text-sm text-gray-500">
                {getMonthName(month)} {year} &middot; Includes New &amp; Used
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!collapsed && onRefresh && (
              <button
                onClick={(e) => { e.stopPropagation(); onRefresh(); }}
                disabled={loading || refreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Refresh sales manager data from DealerBuilt"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
            {!collapsed && showHeaderTotals && summary && (
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(totalGross)}
                </p>
                <p className="text-sm text-gray-500">
                  {summary.totalUnits} Units
                  {' ('}
                  {summary.totalNewUnits} New
                  {(summary.totalSwapUnits ?? 0) > 0 && (
                    <span className="text-blue-500"> + {summary.totalSwapUnits} Swap</span>
                  )}
                  {' / '}
                  {summary.totalUsedUnits} Used
                  {(summary.totalWholesaleUnits ?? 0) > 0 && (
                    <span className="text-purple-500"> + {summary.totalWholesaleUnits} Whlsl</span>
                  )}
                  {')'}
                </p>
              </div>
            )}
            {onToggleCollapse && (
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {!collapsed && <div className="p-4">
        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">Loading sales manager data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-red-800 font-medium">
                  Error Loading Data
                </h4>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Data Display */}
        {!loading && !error && (
          <>
            {managers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UserGroupIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No sales manager data available for this period.</p>
                <p className="text-sm mt-1">
                  Data is loaded nightly from DealerBuilt.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Manager List - 2/3 width on large screens */}
                <div className="lg:col-span-2">
                  <SalesManagerList
                    managers={managers}
                    sortConfig={sortConfig}
                    onSortChange={onSortChange}
                  />
                </div>

                {/* Pie Charts - 1/3 width on large screens, stacked vertically */}
                <div className="lg:col-span-1 flex flex-col gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">
                      Gross Distribution
                    </h4>
                    <SalesManagerPieChart
                      managers={managers}
                      totalGross={totalGross}
                    />
                  </div>

                  {/* vAuto Look to Book — past 60 days, per appraiser */}
                  {(showLookToBook || lookToBookLoading) && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">
                        Look to Book
                      </h4>
                      {lookToBookLoading ? (
                        <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                          Loading…
                        </div>
                      ) : (
                        <LookToBookPieChart
                          appraisers={lookToBookData!.appraisers}
                          totalAppraisals={lookToBookData!.totalAppraisals}
                          totalTakenIn={lookToBookData!.totalTakenIn}
                          totalLookToBook={lookToBookData!.totalLookToBook}
                        />
                      )}
                      <p className="text-[10px] text-gray-400 text-center mt-2">
                        Source:{' '}
                        <a
                          href={
                            lookToBookData?.sourceUrl ??
                            'https://profittime.vauto.app.coxautoinc.com/Va/Dashboard/Provision.aspx?tab=EnterpriseInventory'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-blue-600"
                        >
                          vAuto Appraisal Summary — past 60 days
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>}

      {/* Footer - Last Updated */}
      {!collapsed && summary?.lastUpdated && !loading && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-500 text-center">
            Last updated:{' '}
            {new Date(summary.lastUpdated).toLocaleString('en-US', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </p>
        </div>
      )}
    </div>
  );
};
